import { API_URL } from "../utils/urls"; // PT-REACT-URLS v1
import { useEffect, useRef, useCallback, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import moment from "moment";
import { BsFillPlayFill } from "react-icons/bs";
import { FiCalendar } from "react-icons/fi";
import { HiArrowLongRight } from "react-icons/hi2";
import { placeholderImage, translate, truncateText } from "../utils";
import "./pt-home.css";

/* Shared building blocks for the bhagwa homepage design:
   saffron header bar panels, cards with dates, video lists. */

export const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

/*
 Safe autoplay for Swiper sliders.
 Swiper 8's own Autoplay module can throw "Cannot read properties of undefined
 (reading 'autoplay')" when a slider is destroyed on page change. This timer is
 cleared when the component unmounts and never touches a destroyed slider.
 Usage:  const onSwiper = useSafeAutoplay(5000, true);  <Swiper onSwiper={onSwiper} ...>
*/
export const useSafeAutoplay = (delay = 4000, enabled = true) => {
    const swiperRef = useRef(null);
    useEffect(() => {
        if (!enabled) return undefined;
        const id = setInterval(() => {
            const s = swiperRef.current;
            if (!s || s.destroyed || typeof s.slideNext !== "function") return;
            if (document.hidden) return;
            if (!s.params || (!s.params.loop && s.isEnd)) {
                if (typeof s.slideTo === "function") s.slideTo(0);
            } else {
                s.slideNext();
            }
        }, delay);
        return () => {
            clearInterval(id);
            swiperRef.current = null;
        };
    }, [delay, enabled]);
    return useCallback((s) => {
        swiperRef.current = s;
    }, []);
};

export const formatDate = (value) => {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.format("DD MMM YYYY") : "";
};

export const DateLine = ({ date }) => {
    const text = formatDate(date);
    return text ? (
        <span className="pt-date">
            <FiCalendar aria-hidden="true" /> {text}
        </span>
    ) : null;
};

const stripHtml = (html = "") => String(html).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

// "Dr Pravin Togadia Gallery" panel mein kul kitni photos dikhani hain.
// News kam hon to baaki jagah Admin → Gallery ki photos se bhar di jaati hai.
const GALLERY_LIMIT = 9;
const GALLERY_API = API_URL + "get_gallery";

export const AdSpace = ({ Data, type }) => {
    const ad = Data?.ad_spaces;
    if (!ad || Data.id !== ad.ad_featured_section_id || Data.news_type !== type || !ad.web_ad_image) {
        return null;
    }
    return (
        <div className="ad_spaces pt-ad">
            <div role="link" tabIndex={0} onClick={() => window.open(ad.ad_url, "_blank")}>
                <img className="adimage" src={ad.web_ad_image} alt="ads" />
            </div>
        </div>
    );
};

/* Old-style heading (used on inner pages) */
export const SectionHead = ({ title, description, viewMoreTo }) => (
    <div className="pt-section-head">
        <div>
            <h2 className="pt-section-heading">{title}</h2>
            {description ? <p className="pt-section-desc">{description}</p> : null}
        </div>
        {viewMoreTo ? (
            <Link to={viewMoreTo} className="pt-viewmore" onClick={scrollToTop}>
                {translate("viewMore")}
            </Link>
        ) : null}
    </div>
);

/* Saffron header bar */
export const PanelHead = ({ title, viewAllTo }) => (
    <div className="pt-panel-head">
        <h2>{title}</h2>
        {viewAllTo ? (
            <Link to={viewAllTo} onClick={scrollToTop} className="pt-panel-viewall">
                View All <HiArrowLongRight aria-hidden="true" />
            </Link>
        ) : null}
    </div>
);

export const Panel = ({ title, viewAllTo, children, className = "" }) => (
    <section className={`pt-panel ${className}`}>
        <PanelHead title={title} viewAllTo={viewAllTo} />
        <div className="pt-panel-body">{children}</div>
    </section>
);

export const NewsCard = ({ to, image, title, category, date }) => (
    <Link to={to} className="pt-news-card">
        <span className="pt-news-card-img">
            <img src={image} alt={title || "news"} loading="lazy" onError={placeholderImage} />
            {category ? <span className="pt-featured-category">{truncateText(category, 18)}</span> : null}
        </span>
        <span className="pt-news-card-body">
            <span className="pt-news-card-title">{truncateText(stripHtml(title), 100)}</span>
            <DateLine date={date} />
        </span>
    </Link>
);

export const VideoCard = ({ image, title, onPlay }) => (
    <button type="button" className="pt-video-card" onClick={onPlay} aria-label={`Play video: ${title}`}>
        <span className="pt-video-thumb">
            <img src={image} alt={title || "video"} loading="lazy" onError={placeholderImage} />
            <span className="pt-video-play" aria-hidden="true">
                <BsFillPlayFill size={34} />
            </span>
        </span>
        <span className="pt-video-title">{truncateText(title, 90)}</span>
    </button>
);

/* Compact row: thumbnail + title + date (used in lists) */
export const ListItem = ({ to, onClick, image, title, date, isVideo }) => {
    const inner = (
        <>
            <span className="pt-list-thumb">
                <img src={image} alt={title || "news"} loading="lazy" onError={placeholderImage} />
                {isVideo ? (
                    <span className="pt-list-play" aria-hidden="true">
                        <BsFillPlayFill size={16} />
                    </span>
                ) : null}
            </span>
            <span className="pt-list-body">
                <span className="pt-list-title">{truncateText(stripHtml(title), 80)}</span>
                <DateLine date={date} />
            </span>
        </>
    );
    return to ? (
        <Link to={to} className="pt-list-item">
            {inner}
        </Link>
    ) : (
        <button type="button" className="pt-list-item" onClick={onClick} aria-label={`Play video: ${title}`}>
            {inner}
        </button>
    );
};

/* ---------- One panel for any feature section (news / videos / breaking news) ---------- */
export const SectionPanel = ({ Data, onPlayVideo }) => {
    const title = stripHtml(Data?.title || "");

    if (Data?.videos?.length > 0) {
        const [first, ...rest] = Data.videos;
        return (
            <Panel title={title} viewAllTo={`/video-news-view/${Data.id}`} className="pt-panel--videos">
                <div className="pt-videos-layout">
                    <button
                        type="button"
                        className="pt-video-hero"
                        onClick={() => onPlayVideo(first.content_value, first.type)}
                        aria-label={`Play video: ${stripHtml(first.title)}`}
                    >
                        <span className="pt-video-thumb">
                            <img src={first.image} alt={stripHtml(first.title) || "video"} loading="lazy" onError={placeholderImage} />
                            <span className="pt-video-play" aria-hidden="true">
                                <BsFillPlayFill size={34} />
                            </span>
                        </span>
                        <span className="pt-video-hero-title">{truncateText(stripHtml(first.title), 90)}</span>
                        <DateLine date={first.date} />
                    </button>
                    {rest.length > 0 ? (
                        <div className="pt-list">
                            {rest.slice(0, 3).map((v) => (
                                <ListItem
                                    key={v.id}
                                    isVideo
                                    image={v.image}
                                    title={v.title}
                                    date={v.date}
                                    onClick={() => onPlayVideo(v.content_value, v.type)}
                                />
                            ))}
                        </div>
                    ) : null}
                </div>
            </Panel>
        );
    }

    const isBreaking = !Data?.news?.length && Data?.breaking_news?.length > 0;
    const items = Data?.news?.length ? Data.news : Data?.breaking_news || [];
    if (!items.length) return null;
    const linkFor = (item) => (isBreaking ? `/breaking-news/${item.id}` : `/news/${item.id}/${item.category_id}`);

    // Gallery-type sections (e.g. "Dr Pravin Togadia Gallery") → even square photo grid
    const isGallery = /gallery|गैलरी|ગેલેરી|photo|फोटो/i.test(title);
    if (isGallery) {
        return <GalleryPanel title={title} sectionId={Data.id} items={items} linkFor={linkFor} />;
    }

    return (
        <Panel title={title} viewAllTo={`/view-all/${Data.id}`} className="pt-panel--news">
            {/* cards when the panel is wide, list when it is narrow (CSS container query) */}
            <div className="pt-panel-cards">
                {items.slice(0, 4).map((item) => (
                    <NewsCard
                        key={item.id}
                        to={linkFor(item)}
                        image={item.image}
                        title={item.title}
                        date={item.date}
                    />
                ))}
            </div>
            <div className="pt-list pt-panel-list">
                {items.slice(0, 4).map((item) => (
                    <ListItem key={item.id} to={linkFor(item)} image={item.image} title={item.title} date={item.date} />
                ))}
            </div>
        </Panel>
    );
};

/* ---------- Gallery panel: news ki photos + (kam hon to) Admin Gallery ki photos ---------- */
const GalleryPanel = ({ title, sectionId, items, linkFor }) => {
    const [extra, setExtra] = useState([]);
    const needMore = items.length < GALLERY_LIMIT;

    useEffect(() => {
        if (!needMore) return undefined;
        let active = true;
        axios
            .get(GALLERY_API, { headers: { "Content-Type": "application/json" } })
            .then((res) => {
                const all = res?.data?.data || [];
                const photos = all.filter((img) => img.type === "gallery" && img.image);
                if (active) setExtra(photos);
            })
            .catch((err) => console.error("Gallery photos load error:", err));
        return () => {
            active = false;
        };
    }, [needMore]);

    const newsTiles = items.map((item) => ({
        key: `n-${item.id}`,
        to: linkFor(item),
        image: item.image,
        title: stripHtml(item.title),
    }));
    const galleryTiles = extra.map((p, i) => ({
        key: `g-${p.id || i}`,
        to: "/gallery-image",
        image: p.image,
        title: p.title ? stripHtml(p.title) : "",
    }));
    const tiles = [...newsTiles, ...galleryTiles].slice(0, GALLERY_LIMIT);

    return (
        <Panel title={title} viewAllTo={`/view-all/${sectionId}`} className="pt-panel--gallery">
            <div className="pt-gallery-grid">
                {tiles.map((t) => (
                    <Link key={t.key} to={t.to} onClick={scrollToTop} className="pt-gallery-item" title={t.title}>
                        <img src={t.image} alt={t.title || "photo"} loading="lazy" onError={placeholderImage} />
                        {t.title ? <span className="pt-gallery-caption">{truncateText(t.title, 60)}</span> : null}
                    </Link>
                ))}
            </div>
        </Panel>
    );
};
